import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { adminApi, formatError } from "@/lib/adminApi";
import { tl, trDate } from "@/lib/booking";
import { METHOD, STATUS, dt } from "@/pages/admin/sections/VisitsSection";

const input = "w-full min-h-[44px] rounded-xl border border-line bg-white px-3 text-base";
const EMAIL = { sent: "Gönderildi", failed: "Gönderilemedi" };

export default function VisitDetail({ b, onClose, onUpdated, onDeleted }) {
  const [c, setC] = useState(b.counts);
  const [plate, setPlate] = useState(b.plate);
  const [busy, setBusy] = useState(false);
  const active = b.status === "active";
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      onUpdated((await fn()).data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const voidIt = () => {
    const reason = window.prompt("Geçersiz kılma nedeni (zorunlu, en az 3 karakter):", "");
    if (reason == null) return;
    if (reason.trim().length < 3) return toast.error("Lütfen bir neden yazın.");
    run(() => adminApi.post(`/admin/visits/${b.booking_id}/void`, { reason }), "Kayıt geçersiz kılındı");
  };
  const del = async () => {
    if (!window.confirm(`${b.code} kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/visits/${b.booking_id}`);
      toast.success("Kayıt silindi");
      onDeleted();
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };
  const rows = [
    ["Durum", STATUS[b.status]], ["Tarih", trDate(b.date)], ["Ad soyad", b.name], ["Telefon", b.phone || "–"], ["E-posta", b.email || "–"],
    ["Girişte ödenecek", tl(b.amount_due)], ["Ödeme", b.payment ? `${METHOD[b.payment.method]} · ${tl(b.payment.amount)} · ${b.payment.by}` : "–"],
    ["Giriş", `${dt(b.checked_in_at)}${b.checked_in_by ? ` · ${b.checked_in_by}` : ""}`], ["Kaynak", b.source === "walkin" ? "Gişe satışı" : "Online"],
    ["E-posta durumu", EMAIL[b.email_status?.state] || "–"], ["Oluşturulma", dt(b.created_at)],
    ...(b.cancelled_at ? [["İptal", `${dt(b.cancelled_at)} · ${b.cancelled_by}`]] : []),
    ...(b.voided ? [["Geçersiz kılındı", `${dt(b.voided.at)} · ${b.voided.by} · ${b.voided.reason}`]] : []),
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="visit-detail">
        <DialogTitle className="font-mono text-2xl">{b.code}</DialogTitle>
        <DialogDescription className="sr-only">Ziyaret kaydı ayrıntıları</DialogDescription>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {rows.map(([k, v]) => [<dt key={`${k}t`} className="text-ink-muted">{k}</dt>, <dd key={`${k}d`} className="font-medium" data-testid={`visit-detail-${k.split(" ")[0].toLowerCase()}`}>{v}</dd>])}
        </dl>
        {active && (
          <div className="rounded-2xl border border-line p-4 space-y-3" data-testid="visit-edit">
            <p className="text-sm font-semibold text-wood">Düzenle</p>
            <div className="grid grid-cols-3 gap-2">
              {[["adult", "Yetişkin"], ["child", "Çocuk"], ["free", "Ücretsiz"]].map(([k, l]) => (
                <label key={k} className="text-xs font-semibold space-y-1">{l}<input type="number" min="0" value={c[k]} onChange={(e) => setC({ ...c, [k]: Number(e.target.value) || 0 })} className={input} data-testid={`visit-edit-${k}`} /></label>
              ))}
            </div>
            <label className="block text-xs font-semibold space-y-1">Plaka<input value={plate} onChange={(e) => setPlate(e.target.value)} className={input} data-testid="visit-edit-plate" /></label>
            <button type="button" disabled={busy} onClick={() => run(() => adminApi.patch(`/admin/visits/${b.booking_id}`, { counts: c, plate }), "Kayıt güncellendi")} className="min-h-[44px] px-5 rounded-full bg-forest text-cream text-sm font-semibold disabled:opacity-50" data-testid="visit-edit-save">Kaydet</button>
          </div>
        )}
        {b.adjustments?.length > 0 && (
          <div className="text-sm">
            <p className="font-semibold text-wood mb-1">Değişiklik kaydı</p>
            <ul className="space-y-1" data-testid="visit-adjustments">
              {b.adjustments.map((a) => (
                <li key={a.at} className="text-ink-muted">{dt(a.at)} · {a.by} ({a.where === "gate" ? "girişte" : "panel"}): {a.from.adult}/{a.from.child}/{a.from.free} → {a.to.adult}/{a.to.child}/{a.to.free}, {tl(a.amount_from)} → {tl(a.amount_to)}{a.plate_changed ? " · plaka değişti" : ""}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-2">
          {b.email && <button type="button" disabled={busy} onClick={() => run(() => adminApi.post(`/admin/visits/${b.booking_id}/resend-email`), "E-posta gönderimi denendi")} className="min-h-[44px] px-5 rounded-full border border-line text-sm font-semibold" data-testid="visit-resend-email">E-postayı yeniden gönder</button>}
          {active && <button type="button" disabled={busy} onClick={() => window.confirm("Kayıt iptal edilsin mi? Kontenjan serbest bırakılır.") && run(() => adminApi.post(`/admin/visits/${b.booking_id}/cancel`), "Kayıt iptal edildi")} className="min-h-[44px] px-5 rounded-full border border-[#B42318]/40 text-[#B42318] text-sm font-semibold" data-testid="visit-cancel">İptal et</button>}
          {b.status !== "voided" && <button type="button" disabled={busy} onClick={voidIt} className="min-h-[44px] px-5 rounded-full border border-[#B42318]/40 text-[#B42318] text-sm font-semibold" data-testid="visit-void">Kaydı geçersiz kıl</button>}
          <button type="button" disabled={busy} onClick={del} className="min-h-[44px] px-5 rounded-full bg-[#B42318] text-white text-sm font-semibold" data-testid="visit-delete">Kalıcı olarak sil</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
